import { useTranslation } from "react-i18next";
import { Building2, User } from "lucide-react";

/**
 * Whose patient this is, for a therapist who works in a centre: the centre's
 * (it sees them, can hand them over, charges them) or the therapist's own
 * (the centre never sees them). A therapist who works alone never meets
 * either of these.
 */

interface ChoiceProps {
    centerName: string;
    heldByCenter: boolean;
    onChange: (heldByCenter: boolean) => void;
    /** Distinguishes the radio group when a page holds more than one. */
    name?: string;
    question?: string;
}

/** Asked when a patient or a couple is taken on. The centre's by default. */
export function PatientOwnershipChoice({
    centerName,
    heldByCenter,
    onChange,
    name = "patient-ownership",
    question,
}: ChoiceProps) {
    const { t } = useTranslation();
    const options = [
        {
            value: true,
            label: t("patient.ownership.center", { center: centerName }),
            hint: t("patient.ownership.centerHint"),
        },
        {
            value: false,
            label: t("patient.ownership.personal"),
            hint: t("patient.ownership.personalHint"),
        },
    ];

    return (
        <fieldset>
            <legend className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-400">
                {question ?? t("patient.ownership.question")}
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {options.map((option) => (
                    <label
                        key={String(option.value)}
                        className={`flex cursor-pointer flex-col gap-1 rounded-xl border p-3 text-sm ${
                            heldByCenter === option.value
                                ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                                : "border-gray-200 dark:border-gray-800"
                        }`}
                    >
                        <span className="flex items-center gap-2 font-medium text-content-primary">
                            <input
                                type="radio"
                                name={name}
                                checked={heldByCenter === option.value}
                                onChange={() => onChange(option.value)}
                            />
                            {option.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{option.hint}</span>
                    </label>
                ))}
            </div>
        </fieldset>
    );
}

interface BadgeProps {
    heldByCenter: boolean;
    className?: string;
}

/** "Centre" or "Personal", on a list row or a patient's header. */
export function PatientOwnershipBadge({ heldByCenter, className = "" }: BadgeProps) {
    const { t } = useTranslation();
    return (
        <span
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                heldByCenter
                    ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
                    : "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300"
            } ${className}`}
            title={
                heldByCenter ? t("patient.ownership.centerHint") : t("patient.ownership.personalHint")
            }
        >
            {heldByCenter ? (
                <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
                <User className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {heldByCenter ? t("patient.ownership.centerBadge") : t("patient.ownership.personalBadge")}
        </span>
    );
}
